import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { FiLoader } from 'react-icons/fi';
import Footer from './Footer';
import theme from '../styles/theme';

// Componentes estilizados
const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${theme.colors.background.primary};
`;

const LoadingContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const LoadingCard = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  text-align: center;
  max-width: 400px;
  width: 90%;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border.primary};
  border-top: 3px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin-bottom: ${theme.spacing[2]};
`;

const LoadingSubtext = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
`;

// Rotas permitidas para usuários (não-admin)
const USER_BASE_ROUTES = [
  '/veiculos',
  '/whatsapp/conversas',
  '/whatsapp/contatos',
  '/cliente-sobre',
  '/dashboard/meus-contratos',
  '/dashboard/creditos',
  '/upgrade-plano',
  '/selecionar-cartao-upgrade',
];

const GESTOR_EXTRA_ROUTES = [
  '/usuarios',
  '/dashboard/meus-contratos',
  '/meus-cartoes',
  '/comprar-creditos',
];

const getAllowedRoutesForUser = (permissao) => {
  if (permissao === 'GESTOR') {
    return [...USER_BASE_ROUTES, ...GESTOR_EXTRA_ROUTES];
  }

  return [...USER_BASE_ROUTES];
};

// Verifica se uma rota está permitida para usuários
const isUserAllowedRoute = (pathname, permissao) => {
  const allowedRoutes = getAllowedRoutesForUser(permissao);
  return allowedRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`));
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isLoading, isAdmin, user, hasPermission } = useAuth();
  const location = useLocation();

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <LoadingCard>
            <LoadingSpinner />
            <LoadingText>Verificando acesso...</LoadingText>
            <LoadingSubtext>Aguarde enquanto verificamos suas credenciais</LoadingSubtext>
          </LoadingCard>
        </LoadingContent>
        <Footer />
      </LoadingContainer>
    );
  }

  // Redirecionar para login se não estiver autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se é usuário (não-admin) e a rota requer admin, bloquear
  // Só verificar permissões se o usuário estiver autenticado
  // Rotas de redirecionamento (/, *) devem ser ignoradas na verificação de permissão
  const isRedirectRoute = location.pathname === '/' || location.pathname === '*';
  
  if (isAuthenticated && !isRedirectRoute) {
    const userPermission = user?.permissao_codigo || user?.permissao || null;
    const isGestor = hasPermission ? hasPermission('GESTOR') : (userPermission === 'GESTOR');

    if (requireAdmin && !isAdmin() && !isGestor) {
      return (
        <LoadingContainer>
          <LoadingContent>
            <LoadingCard>
              <LoadingText>Acesso Negado</LoadingText>
              <LoadingSubtext>
                Você não tem permissão para acessar esta página. 
                Entre em contato com um administrador.
              </LoadingSubtext>
            </LoadingCard>
          </LoadingContent>
          <Footer />
        </LoadingContainer>
      );
    }

    // Se é usuário (não-admin), verificar se a rota está permitida
    if (user?.tipo === 'user' && !isAdmin()) {
      // Usar permissao_codigo ou permissao, preferindo permissao_codigo
      const userPermission = user?.permissao_codigo || user?.permissao || null;
      
      if (!isUserAllowedRoute(location.pathname, userPermission)) {
        const allowedRoutes = getAllowedRoutesForUser(userPermission)
          .map((route) => route.replace('/', '').replace('-', ' '))
          .map((name) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '')
          .filter(Boolean)
          .join(', ') || 'Veículos, Contatos, WhatsApp e Info Revenda';

        return (
          <LoadingContainer>
            <LoadingContent>
              <LoadingCard>
                <LoadingText>Acesso Negado</LoadingText>
                <LoadingSubtext>
                  Você não tem permissão para acessar esta página. 
                  Usuários do sistema têm acesso apenas a: {allowedRoutes}.
                </LoadingSubtext>
              </LoadingCard>
            </LoadingContent>
            <Footer />
          </LoadingContainer>
        );
      }
    }
  }

  // Renderizar o componente filho se tudo estiver OK
  return children;
};

export default ProtectedRoute;
